import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { detectFields } from "utils/collegeDocumentService";
import { applyDetectedFields } from "./importFieldsUtils";
import { libraryErrorMessage } from "./libraryErrorMessage";

/**
 * Import fields: read the rectangles the PDF's own form already declares straight into the editor,
 * so an admin does not redraw by hand what the document already describes. Nothing is persisted
 * here, so an unwanted import costs one Back instead of a lost mapping.
 */
export const useImportFields = ({ fileId, fields, setFields, onFieldsReplaced }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const importMutation = useMutation({
    mutationFn: () => detectFields(fileId),
    onSuccess: (detected) => {
      const result = applyDetectedFields(fields, detected);
      if (result.imported === 0) {
        toast.info("This PDF declares no fillable fields, so nothing was imported.");
        return;
      }
      setFields(result.fields);
      onFieldsReplaced?.();
      toast.success(`Loaded ${result.imported} field(s) from the PDF. Save mapping to keep them.`);
    },
    onError: (error) => toast.error(libraryErrorMessage(error, "Could not read this PDF's fields")),
  });

  return {
    importing: importMutation.isLoading,
    confirmOpen,
    replacedFieldCount: fields.length,
    // Only a mapping that already has fields is at risk, so an empty one imports straight away.
    requestImport: () => (fields.length > 0 ? setConfirmOpen(true) : importMutation.mutate()),
    cancelImport: () => setConfirmOpen(false),
    confirmImport: () => {
      setConfirmOpen(false);
      importMutation.mutate();
    },
  };
};
